import {
	forwardRef,
	useEffect,
	useImperativeHandle,
	useMemo,
	useRef
} from "react";
import PropTypes from "prop-types";

// react-table v7 hooks
import { useTable, useFilters, useSortBy, usePagination } from "react-table";

// @mui material components
import LinearProgress from "@mui/material/LinearProgress";
import MenuItem from "@mui/material/MenuItem";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import DataTableHeadCell from "examples/Tables/DataTable/DataTableHeadCell";
import DataTableBodyCell from "examples/Tables/DataTable/DataTableBodyCell";

import PagePagination from "components/DigiHapi/PagePagination";

// Case-insensitive "contains" filter used for client-side filtering
const containsFilter = (rows, ids, filterValue) => {
	const needle = String(filterValue).toLowerCase();
	return rows.filter(row =>
		ids.some(id =>
			String(row.values[id] ?? "")
				.toLowerCase()
				.includes(needle)
		)
	);
};

// Convert a react-table v6 column definition into a v7 one
const toV7Column = column => {
	const { Header, accessor, id, sortable, filterable, Cell, width } = column;
	return {
		Header: Header ?? "",
		accessor,
		id: id || (typeof accessor === "string" ? accessor : undefined),
		width,
		disableSortBy: sortable === false,
		disableFilters: !filterable,
		filter: containsFilter,
		...(Cell && {
			// v6 Cell props: { value, original, row, index }
			Cell: ({ value, row }) =>
				Cell({
					value,
					original: row.original,
					row: row.original,
					index: row.index
				})
		})
	};
};

const getSortedState = column => {
	if (!column.canSort) {
		return false;
	}
	if (!column.isSorted) {
		return "none";
	}
	return column.isSortedDesc ? "desc" : "asce";
};

/**
 * MD3 table built on react-table v7, exposing the subset of the react-table v6
 * API used by the admin pages:
 * - columns: { Header, accessor, id, sortable, filterable, Cell, width }
 * - defaultSorted, defaultPageSize, pageSizeOptions, loading, noDataText
 * - manual + pages + onFetchData(state) for server-side pagination/sort/filters
 *   (state: { page, pageSize, sorted, filtered, pages, data, sortedData })
 * - page: controlled page index (0-based)
 * - TbodyComponent / TrComponent: custom body/row elements (e.g. drag & drop);
 *   they must render a <tbody> / <tr> (MUI TableBody / TableRow) and receive
 *   `rowInfo` ({ original, index }) for rows
 * - ref: { state, setState({ page }, callback), fireFetchData() }
 */
const DataTable = forwardRef(function DataTable(
	{
		data,
		columns,
		defaultSorted = [],
		defaultPageSize = 10,
		pageSizeOptions = [5, 10, 20, 25, 50, 100],
		manual = false,
		pages,
		page: controlledPage,
		onFetchData,
		loading = false,
		noDataText = "Aucune donnée",
		loadingText = "Chargement...",
		showPaginationTop = false,
		showPaginationBottom = true,
		TbodyComponent,
		TrComponent
	},
	ref
) {
	const v7Columns = useMemo(() => columns.map(toV7Column), [columns]);

	const {
		getTableProps,
		getTableBodyProps,
		headerGroups,
		prepareRow,
		rows,
		page,
		pageCount,
		gotoPage,
		setPageSize,
		state: { pageIndex, pageSize, sortBy, filters }
	} = useTable(
		{
			columns: v7Columns,
			data,
			manualPagination: manual,
			manualSortBy: manual,
			manualFilters: manual,
			// Only pass pageCount in manual mode: an explicit `undefined` breaks
			// client-side page navigation in react-table v7
			...(manual && { pageCount: pages ?? -1 }),
			disableMultiSort: true,
			// Keep page / sort / filters when data is refreshed (react-table v6 behavior)
			autoResetPage: false,
			autoResetSortBy: false,
			autoResetFilters: false,
			initialState: {
				pageIndex: controlledPage ?? 0,
				pageSize: defaultPageSize,
				sortBy: defaultSorted.map(({ id, desc }) => ({ id, desc: !!desc }))
			}
		},
		useFilters,
		useSortBy,
		usePagination
	);

	// Follow the controlled page index
	useEffect(() => {
		if (controlledPage !== undefined && controlledPage !== pageIndex) {
			gotoPage(controlledPage);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [controlledPage]);

	// v6-like state given to onFetchData and exposed through the ref
	const fetchStateRef = useRef();
	fetchStateRef.current = {
		page: pageIndex,
		pageSize,
		sorted: sortBy.map(({ id, desc }) => ({ id, desc: !!desc })),
		filtered: filters.map(({ id, value }) => ({ id, value })),
		pages: pageCount,
		data: rows.map(row => row.original),
		sortedData: rows.map(row => ({ ...row.values, _original: row.original }))
	};
	const onFetchDataRef = useRef(onFetchData);
	onFetchDataRef.current = onFetchData;

	const fireFetchData = () => {
		if (onFetchDataRef.current) {
			onFetchDataRef.current(fetchStateRef.current);
		}
	};

	// Fetch on mount and whenever page / size / sort / filters change;
	// client-side tables also notify when their number of rows changes.
	// (Pages rebuild `data` on every render, so its identity cannot be a dependency.)
	useEffect(
		fireFetchData,
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[pageIndex, pageSize, sortBy, filters, manual ? 0 : data.length]
	);

	useImperativeHandle(ref, () => ({
		get state() {
			return fetchStateRef.current;
		},
		setState: ({ page: newPage } = {}, callback) => {
			if (newPage !== undefined) {
				gotoPage(newPage);
			}
			if (callback) {
				setTimeout(callback);
			}
		},
		fireFetchData
	}));

	const hasFilters = headerGroups.some(group =>
		group.headers.some(column => column.canFilter)
	);
	const Body = TbodyComponent || TableBody;
	const sizeOptions = pageSizeOptions.includes(defaultPageSize)
		? pageSizeOptions
		: [...pageSizeOptions, defaultPageSize].sort((a, b) => a - b);

	const controls = (
		<MDBox
			display="flex"
			flexWrap="wrap"
			justifyContent="space-between"
			alignItems="center"
			gap={2}
			px={2}
			py={1.5}
		>
			<MDBox display="flex" alignItems="center">
				<MDInput
					select
					size="small"
					value={pageSize}
					onChange={e => setPageSize(Number(e.target.value))}
					sx={{
						minWidth: "5rem",
						// Restore the vertical padding and arrow removed by the MD3 theme
						"& .MuiSelect-select.MuiSelect-select.MuiInputBase-input": {
							padding: "8px 28px 8px 10px !important",
							minHeight: "auto !important"
						},
						"& .MuiSelect-icon": { display: "inline-block", right: "4px" }
					}}
				>
					{sizeOptions.map(option => (
						<MenuItem key={option} value={option}>
							{option}
						</MenuItem>
					))}
				</MDInput>
				<MDTypography variant="caption" color="secondary" ml={1}>
					lignes par page
				</MDTypography>
			</MDBox>
			{pageCount > 0 && (
				<MDTypography variant="button" color="secondary" fontWeight="regular">
					Page {pageIndex + 1} sur {pageCount}
				</MDTypography>
			)}
			<PagePagination
				currentPage={pageIndex + 1}
				totalPages={pageCount}
				changeCurrentPage={newPage => gotoPage(newPage - 1)}
			/>
		</MDBox>
	);

	return (
		<TableContainer sx={{ boxShadow: "none", position: "relative" }}>
			{showPaginationTop && controls}
			{loading && (
				<LinearProgress
					color="primary"
					sx={{ position: "absolute", left: 0, right: 0, height: 3 }}
					aria-label={loadingText}
				/>
			)}
			<Table {...getTableProps()}>
				<MDBox component="thead">
					{headerGroups.map(headerGroup => {
						const { key: groupKey, ...groupProps } =
							headerGroup.getHeaderGroupProps();
						return (
							<TableRow key={groupKey} {...groupProps}>
								{headerGroup.headers.map(column => {
									const { key: columnKey, ...columnProps } =
										column.getHeaderProps(column.getSortByToggleProps());
									return (
										<DataTableHeadCell
											key={columnKey}
											{...columnProps}
											width={column.width || "auto"}
											sorted={getSortedState(column)}
										>
											{column.render("Header")}
										</DataTableHeadCell>
									);
								})}
							</TableRow>
						);
					})}
					{hasFilters &&
						headerGroups.map((headerGroup, index) => (
							<TableRow key={`filters-${index}`}>
								{headerGroup.headers.map(column => (
									<MDBox component="th" key={column.id} px={2} pb={1}>
										{column.canFilter && (
											<MDInput
												size="small"
												fullWidth
												placeholder="Filtrer…"
												value={column.filterValue || ""}
												onChange={e =>
													column.setFilter(e.target.value || undefined)
												}
											/>
										)}
									</MDBox>
								))}
							</TableRow>
						))}
				</MDBox>
				<Body {...getTableBodyProps()}>
					{page.map(row => {
						prepareRow(row);
						const { key, ...rowProps } = row.getRowProps();
						const cells = row.cells.map(cell => {
							const { key: cellKey, ...cellProps } = cell.getCellProps();
							return (
								<DataTableBodyCell key={cellKey} {...cellProps}>
									{cell.render("Cell")}
								</DataTableBodyCell>
							);
						});
						return TrComponent ? (
							<TrComponent
								key={key}
								rowInfo={{ original: row.original, index: row.index }}
							>
								{cells}
							</TrComponent>
						) : (
							<TableRow key={key} hover {...rowProps}>
								{cells}
							</TableRow>
						);
					})}
					{!loading && page.length === 0 && (
						<TableRow>
							<DataTableBodyCell noBorder>
								<MDTypography variant="button" color="text">
									{noDataText}
								</MDTypography>
							</DataTableBodyCell>
						</TableRow>
					)}
				</Body>
			</Table>
			{showPaginationBottom && controls}
		</TableContainer>
	);
});

DataTable.propTypes = {
	data: PropTypes.array.isRequired,
	columns: PropTypes.arrayOf(PropTypes.object).isRequired,
	defaultSorted: PropTypes.arrayOf(
		PropTypes.shape({ id: PropTypes.string, desc: PropTypes.bool })
	),
	defaultPageSize: PropTypes.number,
	pageSizeOptions: PropTypes.arrayOf(PropTypes.number),
	manual: PropTypes.bool,
	pages: PropTypes.number,
	page: PropTypes.number,
	onFetchData: PropTypes.func,
	loading: PropTypes.bool,
	noDataText: PropTypes.node,
	loadingText: PropTypes.string,
	showPaginationTop: PropTypes.bool,
	showPaginationBottom: PropTypes.bool,
	TbodyComponent: PropTypes.elementType,
	TrComponent: PropTypes.elementType
};

export default DataTable;
